function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  const summaryContent = summary.nextElementSibling;
  if (summaryContent && summaryContent.getAttribute('id')) {
    summary.setAttribute('aria-controls', summaryContent.id);
  }

  summary.addEventListener('click', (event) => {
    const details = event.currentTarget.closest('details');
    if (!details) return;
    event.currentTarget.setAttribute('aria-expanded', !details.hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

document.querySelectorAll('.lang-switch__dropdown').forEach((details) => {
  const summary = details.querySelector('summary');
  if (!summary) return;
  let closeTimerId;

  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', details.hasAttribute('open'));

  details.addEventListener('toggle', () => {
    summary.setAttribute('aria-expanded', details.hasAttribute('open'));
  });

  details.addEventListener('mouseenter', () => {
    if (closeTimerId) clearTimeout(closeTimerId);
    if (!details.hasAttribute('open')) details.setAttribute('open', '');
  });

  details.addEventListener('mouseleave', () => {
    if (closeTimerId) clearTimeout(closeTimerId);
    closeTimerId = setTimeout(() => {
      if (!details.hasAttribute('open')) return;
      if (details.matches(':hover')) return;
      if (details.contains(document.activeElement)) return;
      details.removeAttribute('open');
    }, 120);
  });

  details.addEventListener('focusout', () => {
    requestAnimationFrame(() => {
      if (!details.hasAttribute('open')) return;
      if (details.contains(document.activeElement)) return;
      details.removeAttribute('open');
    });
  });
});

const AGE_RESTRICTED_TAG = '18禁';
const ageGateProductTagCache = new Map();

function getAgeGateMessage(link) {
  return (
    (link && link.getAttribute('data-age-message')) ||
    (window.cThemeConfig && window.cThemeConfig.ageGatePrompt) ||
    'This product is restricted to adults. Are you 18 or older?'
  );
}

function productTagsIncludeAdult(tags) {
  if (!tags) return false;
  if (Array.isArray(tags)) {
    return tags.some((tag) => `${tag}`.trim() === AGE_RESTRICTED_TAG);
  }
  return `${tags}`
    .split(',')
    .map((tag) => tag.trim())
    .includes(AGE_RESTRICTED_TAG);
}

function linkRequiresAgeCheck(link) {
  if (!link) return false;
  if (link.hasAttribute('data-age-check')) return true;
  return productTagsIncludeAdult(link.getAttribute('data-product-tags'));
}

function getProductHandleFromLink(link) {
  if (!link) return '';
  try {
    const match = new URL(link.href, window.location.href).pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  } catch (_error) {
    const match = `${link.getAttribute('href') || ''}`.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }
}

function fetchProductTagsForAgeGate(handle) {
  if (!handle) return Promise.resolve([]);
  if (ageGateProductTagCache.has(handle)) {
    return ageGateProductTagCache.get(handle);
  }

  const request = fetch(`/products/${encodeURIComponent(handle)}.js`, {
    credentials: 'same-origin',
    cache: 'force-cache',
  })
    .then((response) => (response.ok ? response.json() : null))
    .then((productData) => (productData && Array.isArray(productData.tags) ? productData.tags : []))
    .catch(() => []);

  ageGateProductTagCache.set(handle, request);
  return request;
}

function linkUsesDeferredProductAgeCheck(link) {
  if (!link) return false;
  return Boolean(
    link.closest('[id^="ranking-list--"]') ||
      link.closest('[id^="sa-079-ur-viewed-products-section-"]') ||
      link.classList.contains('ranking-link')
  );
}

function linkNeedsDeferredAgeCheck(link) {
  if (!link || linkRequiresAgeCheck(link)) return false;

  try {
    const pathname = new URL(link.href, window.location.href).pathname;
    if (!pathname.includes('/products/')) return false;
  } catch (_error) {
    return false;
  }

  return linkUsesDeferredProductAgeCheck(link);
}

function denyNavigation(event) {
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

function confirmAdultAccess(link) {
  return window.confirm(getAgeGateMessage(link));
}

document.addEventListener(
  'click',
  (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest('a[href]');
    if (!link) return;

    if (linkRequiresAgeCheck(link)) {
      if (!confirmAdultAccess(link)) {
        denyNavigation(event);
      }
      return;
    }

    if (!linkNeedsDeferredAgeCheck(link)) return;

    const handle = getProductHandleFromLink(link);
    if (!handle) return;

    denyNavigation(event);

    fetchProductTagsForAgeGate(handle).then((tags) => {
      if (productTagsIncludeAdult(tags)) {
        link.setAttribute('data-age-check', '');
        if (!link.getAttribute('data-age-message')) {
          link.setAttribute('data-age-message', getAgeGateMessage(link));
        }
        if (!confirmAdultAccess(link)) return;
      }

      if (window.NitoroPageLoader && typeof window.NitoroPageLoader.navigate === 'function') {
        window.NitoroPageLoader.navigate(link.href, 'assign');
      } else if (window.NitoroPageLoader && typeof window.NitoroPageLoader.show === 'function') {
        window.NitoroPageLoader.show();
        window.location.assign(link.href);
      } else {
        window.location.assign(link.href);
      }
    });
  },
  true
);

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    const maxValue = this.input.max ? parseInt(this.input.max, 10) : null;

    if (event.target.name === 'plus') {
      if (maxValue !== null && !Number.isNaN(maxValue) && parseInt(previousValue, 10) >= maxValue) {
        this.showMaxLimitMessage(maxValue);
        return;
      }
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }

    if (
      event.target.name === 'plus' &&
      maxValue !== null &&
      !Number.isNaN(maxValue) &&
      parseInt(this.input.value, 10) >= maxValue &&
      previousValue === this.input.value
    ) {
      this.showMaxLimitMessage(maxValue);
    }
  }

  showMaxLimitMessage(maxValue) {
    let message = this.input.dataset.limitMessage;
    if (!message && window.quickOrderListStrings?.max_error) {
      message = window.quickOrderListStrings.max_error.replace('[max]', String(maxValue));
    }
    if (!message) {
      message = `You can only add up to ${maxValue}.`;
    }
    window.alert(message);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

/**
 * UR: Recently Viewed Products app card normalizer
 * Aligns app-generated card text/price presentation with theme product cards.
 */
(() => {
  const SECTION_SELECTOR = '[id^="sa-079-ur-viewed-products-section-"]';
  const GRID_SELECTOR = '[id^="sa-079-ur-viewed-products-grid-"]';
  const HEADING_SELECTOR = '.sa-079-ur-viewed-products-heading';
  const RECENTLY_VIEWED_DEFAULT_HEADINGS = new Set(['最近チェックした商品', '最近閲覧した商品']);
  const ITEM_SELECTOR = '.sa-079-ur-viewed-products-grid-item';
  const PRICE_SELECTOR = '.sa-079-ur-viewed-products-price';
  const COMPARE_SELECTOR = '.sa-079-ur-viewed-products-price-item--compare';
  const CURRENT_SELECTOR = '.sa-079-ur-viewed-products-price-item:not(.sa-079-ur-viewed-products-price-item--compare)';
  const TITLE_SELECTOR = '.sa-079-ur-viewed-products-card-information__text';
  const TAG_WRAP_CLASS = 'ur-tags';
  const STOCK_WRAP_CLASS = 'ur-stock-wrap';
  const STOCK_META_SECTION = 'c_product-stock-meta';
  const TAX_CLASS = 'ur-tax-included';
  const DISCOUNT_CLASS = 'ur-discount-rate';
  const PRODUCT_WORK_CACHE_PREFIX = 'c_ur_product_work:';
  const productCache = new Map();
  const productPageMetaCache = new Map();
  const observerMap = new WeakMap();
  const sectionObserverMap = new WeakMap();
  const gridNormalizeTimerMap = new WeakMap();
  const dragBoundMap = new WeakMap();
  const scrollbarBoundMap = new WeakMap();

  const findUrGrids = () => document.querySelectorAll(GRID_SELECTOR);

  const scheduleGridNormalize = (grid, delayMs = 0) => {
    if (!grid) return;
    const existing = gridNormalizeTimerMap.get(grid);
    if (existing) window.clearTimeout(existing);

    const timerId = window.setTimeout(() => {
      gridNormalizeTimerMap.delete(grid);
      normalizeAllInGrid(grid).then(() => syncUrScrollbar(grid));
    }, delayMs);

    gridNormalizeTimerMap.set(grid, timerId);
  };

  const getNumber = (text) => {
    if (!text) return 0;
    const n = parseInt(String(text).replace(/[^\d]/g, ''), 10);
    return Number.isNaN(n) ? 0 : n;
  };

  const formatYen = (value) => `¥${Math.max(0, Number(value) || 0).toLocaleString('ja-JP')}`;

  const isJaLocale = () => (document.documentElement.lang || '').toLowerCase().startsWith('ja');

  const localizeRecentlyViewedHeading = (root = document) => {
    const title = window.cThemeStrings?.recentlyViewedTitle;
    if (!title) return;

    root.querySelectorAll(HEADING_SELECTOR).forEach((heading) => {
      const current = (heading.textContent || '').trim();
      if (!current || RECENTLY_VIEWED_DEFAULT_HEADINGS.has(current)) {
        heading.textContent = title;
      }
    });
  };

  const toTaxIncluded = (rawYen) => {
    // Tax-exclusive base price * tax multiplier, with rounding.
    // The multiplier is injected from theme.liquid (single source: c-tax-multiplier).
    const multiplier = Number(window.cThemeConfig && window.cThemeConfig.taxMultiplier) || 1.1;
    return Math.round((Number(rawYen) || 0) * multiplier);
  };

  const ensureTaxLabel = (priceContainer) => {
    if (!priceContainer) return null;
    let tax = priceContainer.querySelector(`.${TAX_CLASS}`);
    if (!tax) tax = document.createElement('span');
    tax.className = TAX_CLASS;
    tax.textContent = isJaLocale() ? '(税込)' : 'tax included';
    return tax;
  };

  const getProductHandleFromItem = (item) => {
    if (!item) return null;
    const link = item.querySelector('a.full-unstyled-link[href*="/products/"]');
    if (!link) return null;

    let handle = '';
    try {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      const parts = url.pathname.split('/products/');
      if (parts[1]) handle = decodeURIComponent(parts[1]).split('/')[0];
    } catch (e) {
      return null;
    }
    return handle || null;
  };

  const getProductJsonFromHandle = async (handle) => {
    if (!handle) return null;

    let task = productCache.get(handle);
    if (!task) {
      try {
        const root = getStorefrontRoot();
        task = fetch(`${root}products/${encodeURIComponent(handle)}.js`)
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null);
        productCache.set(handle, task);
      } catch (e) {
        return null;
      }
    }
    const json = await task;
    return json || null;
  };

  const getStorefrontRoot = () => {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof root === 'string' && root.length ? root : '/';
  };

  const getProductPageUrl = (handle) => {
    return `${getStorefrontRoot()}products/${encodeURIComponent(handle)}`;
  };

  const parseStockMetaFromElement = (stockEl) => {
    if (!stockEl) return null;
    return {
      saleType: stockEl.getAttribute('data-sale-type') || '',
      preorderStart: stockEl.getAttribute('data-preorder-start') || '',
      preorderEnd: stockEl.getAttribute('data-preorder-end') || '',
      inventoryQty: stockEl.getAttribute('data-inventory-qty') || '',
    };
  };

  const parseUrPageMetaFromHtml = (html) => {
    if (!html) return { productWork: '', stock: null };
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const stockEl =
      doc.querySelector('.js-ur-stock-meta') ||
      doc.querySelector('.detail-stock.js-stock-status') ||
      doc.querySelector('.js-stock-status[data-stock-mode="detail"]');
    const productWorkEl = doc.querySelector('[data-product-work]');
    const productWork =
      (productWorkEl && productWorkEl.getAttribute('data-product-work')) ||
      (stockEl && stockEl.getAttribute('data-product-work')) ||
      '';
    return {
      productWork: String(productWork || '').trim(),
      stock: parseStockMetaFromElement(stockEl),
    };
  };

  const parseStockMetaFromHtml = (html) => parseUrPageMetaFromHtml(html).stock;

  const buildStockMetaFromProductJson = (json) => {
    if (!json) return null;

    const fromVariants = getTotalInventoryFromJson(json);
    let inventoryQty = fromVariants;
    if (inventoryQty <= 0) {
      if (json.available === false) inventoryQty = 0;
      else if (json.available === true) inventoryQty = 21;
    }

    return {
      saleType: 'normal',
      preorderStart: '',
      preorderEnd: '',
      inventoryQty: String(Math.max(0, inventoryQty)),
    };
  };

  const fetchUrPageMetaFromSection = async (handle) => {
    const url = `${getProductPageUrl(handle)}?sections=${STOCK_META_SECTION}`;
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const raw = await res.text();
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || !data.sections) return null;
      return parseUrPageMetaFromHtml(data.sections[STOCK_META_SECTION]);
    } catch (e) {
      return null;
    }
  };

  const fetchUrPageMetaFromProductPage = async (handle) => {
    try {
      const res = await fetch(getProductPageUrl(handle));
      if (!res.ok) return null;
      const html = await res.text();
      return parseUrPageMetaFromHtml(html);
    } catch (e) {
      return null;
    }
  };

  const readCachedProductWork = (handle) => {
    if (!handle) return '';
    try {
      return String(sessionStorage.getItem(`${PRODUCT_WORK_CACHE_PREFIX}${handle}`) || '').trim();
    } catch (e) {
      return '';
    }
  };

  const writeCachedProductWork = (handle, productWork) => {
    const work = String(productWork || '').trim();
    if (!handle || !work) return;
    try {
      sessionStorage.setItem(`${PRODUCT_WORK_CACHE_PREFIX}${handle}`, work);
    } catch (e) {
      // Ignore storage errors in private browsing.
    }
  };

  const seedProductWorkCacheFromPage = (root = document) => {
    root.querySelectorAll('.product-card[data-product-handle]').forEach((card) => {
      const handle = String(card.getAttribute('data-product-handle') || '').trim();
      const workEl = card.querySelector('.product-work');
      const work = workEl ? String(workEl.textContent || '').trim() : '';
      if (handle && work) writeCachedProductWork(handle, work);
    });
  };

  const getProductPageMetaFromHandle = async (handle) => {
    if (!handle) return { productWork: '', stock: null };

    let task = productPageMetaCache.get(handle);
    if (!task) {
      task = (async () => {
        const sectionMeta = await fetchUrPageMetaFromSection(handle);
        if (sectionMeta && sectionMeta.productWork) {
          writeCachedProductWork(handle, sectionMeta.productWork);
        }
        if (sectionMeta && (sectionMeta.stock || sectionMeta.productWork)) {
          return sectionMeta;
        }

        const pageMeta = await fetchUrPageMetaFromProductPage(handle);
        if (pageMeta && pageMeta.productWork) {
          writeCachedProductWork(handle, pageMeta.productWork);
        }
        if (pageMeta && (pageMeta.stock || pageMeta.productWork)) {
          return pageMeta;
        }

        const cachedWork = readCachedProductWork(handle);
        if (cachedWork) {
          return { productWork: cachedWork, stock: pageMeta ? pageMeta.stock : null };
        }

        return { productWork: '', stock: pageMeta ? pageMeta.stock : null };
      })()
        .catch(() => {
          const cachedWork = readCachedProductWork(handle);
          return { productWork: cachedWork, stock: null };
        })
        .then((meta) => {
          if (!meta || (!meta.productWork && !meta.stock)) {
            productPageMetaCache.delete(handle);
          }
          return meta || { productWork: '', stock: null };
        });

      productPageMetaCache.set(handle, task);
    }

    return (await task) || { productWork: '', stock: null };
  };

  const getTotalInventoryFromJson = (json) => {
    if (!json) return 0;
    if (typeof json.total_inventory === 'number') return Math.max(0, json.total_inventory);

    if (!Array.isArray(json.variants) || !json.variants.length) return 0;
    return json.variants.reduce((sum, variant) => {
      const qty = Number(variant && variant.inventory_quantity);
      return sum + (Number.isFinite(qty) ? Math.max(0, qty) : 0);
    }, 0);
  };

  const getInventoryQty = (json, stockMeta) => {
    if (stockMeta && stockMeta.inventoryQty !== '' && stockMeta.inventoryQty != null) {
      const parsed = Number.parseInt(`${stockMeta.inventoryQty}`, 10);
      if (Number.isFinite(parsed)) return Math.max(0, parsed);
    }

    const fromJson = getTotalInventoryFromJson(json);
    if (fromJson > 0) return fromJson;

    if (json && json.available === false) return 0;
    if (json && json.available === true) return 21;

    return 0;
  };

  const getUrStockLabels = () => {
    const strings = window.cThemeStrings || {};
    return {
      stockIn: strings.stockIn || '在庫あり',
      stockLow: strings.stockLow || '在庫わずか',
      stockOut: strings.stockOut || '在庫切れ',
      stockPreorder: strings.stockPreorder || '予約受付中',
      stockPreorderLow: strings.stockPreorderLow || '予約わずか',
      stockPreorderClosed: strings.stockPreorderClosed || '予約受付終了',
      stockPlanned: strings.stockPlanned || '販売予定',
      stockEnd: strings.stockEnd || '販売終了',
    };
  };

  const parseUrSaleType = (value) => {
    const raw = `${value || ''}`.trim().toLowerCase();
    if (raw.includes('preorder') || raw.includes('予約')) return 'preorder';
    if (raw.includes('normal') || raw.includes('通常')) return 'normal';
    return 'normal';
  };

  const parseUrDate = (value) => {
    const raw = `${value || ''}`.trim();
    if (!raw) return null;

    const direct = new Date(raw);
    if (!Number.isNaN(direct.getTime())) return direct;

    const normalized = raw
      .replace(/[()（）]/g, ' ')
      .replace(/年/g, '-')
      .replace(/月/g, '-')
      .replace(/日/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const fallback = new Date(normalized);
    if (!Number.isNaN(fallback.getTime())) return fallback;

    return null;
  };

  const resolveUrStockStatus = ({ saleType, qty, startDate, endDate, now }) => {
    const hasEnded = endDate && endDate.getTime() < now.getTime();
    const startsInFuture = startDate && startDate.getTime() > now.getTime();

    if (hasEnded) return 'end';
    if (startsInFuture) return 'planned';

    if (saleType === 'preorder') {
      if (qty <= 0) return 'preorder_closed';
      if (qty <= 20) return 'preorder_low';
      return 'preorder';
    }

    if (qty <= 0) return 'out';
    if (qty <= 20) return 'low';
    return 'in';
  };

  const getUrStockStatusClass = (status) => {
    if (status === 'in' || status === 'preorder') return 'product-stock--in';
    if (status === 'low' || status === 'preorder_low') return 'product-stock--low';
    if (status === 'planned') return 'product-stock--planned';
    if (status === 'out' || status === 'preorder_closed' || status === 'end') return 'product-stock--end';
    return 'product-stock--in';
  };

  const applyUrStockStatusLabel = (stockEl) => {
    if (!stockEl) return;

    const labels = getUrStockLabels();
    const saleType = parseUrSaleType(stockEl.dataset.saleType || stockEl.getAttribute('data-sale-type'));
    const qty = Number.parseInt(`${stockEl.dataset.inventoryQty || '0'}`, 10);
    const safeQty = Number.isFinite(qty) ? qty : 0;
    const startDate = parseUrDate(stockEl.dataset.preorderStart || stockEl.getAttribute('data-preorder-start'));
    const endDate = parseUrDate(stockEl.dataset.preorderEnd || stockEl.getAttribute('data-preorder-end'));
    const status = resolveUrStockStatus({
      saleType,
      qty: safeQty,
      startDate,
      endDate,
      now: new Date(),
    });

    const labelMap = {
      in: labels.stockIn,
      low: labels.stockLow,
      out: labels.stockOut,
      preorder: labels.stockPreorder,
      preorder_low: labels.stockPreorderLow,
      preorder_closed: labels.stockPreorderClosed,
      planned: labels.stockPlanned,
      end: labels.stockEnd,
    };

    const nextLabel = labelMap[status] || '';
    const nextClass = getUrStockStatusClass(status);
    const stockClasses = [
      'product-stock--in',
      'product-stock--low',
      'product-stock--preorder',
      'product-stock--out',
      'product-stock--end',
      'product-stock--planned',
    ];
    const currentClass = stockClasses.find((cls) => stockEl.classList.contains(cls)) || '';

    // Avoid visible flicker when retries re-apply the same final status.
    if (stockEl.textContent === nextLabel && currentClass === nextClass) {
      stockEl.dataset.stockReady = '1';
      return;
    }

    stockEl.textContent = nextLabel;
    stockEl.classList.remove(...stockClasses);
    stockEl.classList.add(nextClass);
    stockEl.dataset.stockReady = '1';
  };

  const placeUrStockWrap = (item, stockWrap) => {
    if (!item || !stockWrap) return;

    const infoWrapper =
      item.querySelector('.sa-079-ur-viewed-products-card-information__wrapper') ||
      item.querySelector('.sa-079-ur-viewed-products-card-information');
    const tagsEl = item.querySelector(`.${TAG_WRAP_CLASS}`);
    const priceContainer = item.querySelector(PRICE_SELECTOR);

    if (infoWrapper) {
      if (tagsEl && infoWrapper.contains(tagsEl)) {
        tagsEl.insertAdjacentElement('beforebegin', stockWrap);
        return;
      }

      if (priceContainer && infoWrapper.contains(priceContainer)) {
        priceContainer.insertAdjacentElement('afterend', stockWrap);
        return;
      }

      infoWrapper.appendChild(stockWrap);
      return;
    }

    const cardWrapper = item.querySelector('.sa-079-ur-viewed-products-card-wrapper');
    if (cardWrapper) {
      cardWrapper.appendChild(stockWrap);
    }
  };

  const ensureStockStatus = (item, json, pageMeta) => {
    if (!item) return;

    const priceContainer = item.querySelector(PRICE_SELECTOR);
    const infoWrapper =
      item.querySelector('.sa-079-ur-viewed-products-card-information__wrapper') ||
      item.querySelector('.sa-079-ur-viewed-products-card-information');
    if (!priceContainer && !infoWrapper) return;

    // Only paint from authoritative section/page meta.
    // product.js fallback (available→qty 21 / saleType normal) shows the wrong tag
    // for preorder products and must not be locked in as final.
    const stockMeta = pageMeta && pageMeta.stock;
    if (!stockMeta) return;

    const labels = getUrStockLabels();
    const nextSaleType = stockMeta.saleType || '';
    const nextPreorderStart = stockMeta.preorderStart || '';
    const nextPreorderEnd = stockMeta.preorderEnd || '';
    const nextInventoryQty = String(getInventoryQty(json, stockMeta));
    const stockFingerprint = [
      nextSaleType,
      nextPreorderStart,
      nextPreorderEnd,
      nextInventoryQty,
    ].join('|');

    let stockWrap = item.querySelector(`.${STOCK_WRAP_CLASS}`);
    let stockEl = stockWrap ? stockWrap.querySelector('.js-stock-status') : null;

    if (
      stockEl &&
      item.dataset.urStockBound === '1' &&
      stockEl.dataset.urStockFingerprint === stockFingerprint &&
      stockEl.dataset.stockReady === '1'
    ) {
      return;
    }

    if (!stockWrap) {
      stockWrap = document.createElement('div');
      stockWrap.className = `${STOCK_WRAP_CLASS} product-stock-wrap`;
    }

    placeUrStockWrap(item, stockWrap);

    if (!stockEl) {
      stockEl = document.createElement('span');
      stockEl.className = 'product-stock js-stock-status';
      stockEl.dataset.stockMode = 'card';
      stockWrap.appendChild(stockEl);
    }

    stockEl.dataset.saleType = nextSaleType;
    stockEl.dataset.preorderStart = nextPreorderStart;
    stockEl.dataset.preorderEnd = nextPreorderEnd;
    stockEl.dataset.inventoryQty = nextInventoryQty;
    stockEl.dataset.labelStockIn = labels.stockIn;
    stockEl.dataset.labelStockLow = labels.stockLow;
    stockEl.dataset.labelStockOut = labels.stockOut;
    stockEl.dataset.labelStockPreorder = labels.stockPreorder;
    stockEl.dataset.labelStockPreorderLow = labels.stockPreorderLow;
    stockEl.dataset.labelStockPreorderClosed = labels.stockPreorderClosed;
    stockEl.dataset.labelStockPlanned = labels.stockPlanned;
    stockEl.dataset.labelStockEnd = labels.stockEnd;
    stockEl.dataset.urStockFingerprint = stockFingerprint;

    applyUrStockStatusLabel(stockEl);
    item.dataset.urStockBound = '1';
  };

  const isUrSectionVisible = (section) => {
    if (!section) return true;
    if (section.style.display === 'none') return false;
    return section.offsetParent !== null || section.getClientRects().length > 0;
  };

  const refreshUrStockStatusLabels = () => {
    // Only reaffirm UR recently-viewed labels. Do not call NitoroStockStatus.init()
    // here — that rewrites every .js-stock-status on the page and can flash listing cards.
    document
      .querySelectorAll(`${SECTION_SELECTOR} .${STOCK_WRAP_CLASS} .js-stock-status`)
      .forEach((stockEl) => applyUrStockStatusLabel(stockEl));
  };

  const removeLegacyTagNodes = (item) => {
    const wrapper = item.querySelector('.sa-079-ur-viewed-products-card-information__wrapper');
    if (!wrapper) return;

    wrapper.querySelectorAll(`.${TAG_WRAP_CLASS}`).forEach((el) => el.remove());

    // Remove only duplicate NEW text nodes from app output.
    // Keep other labels such as 国内限定 / 18禁 so we can safely parse them.
    wrapper.querySelectorAll('span, div, p').forEach((el) => {
      const text = (el.textContent || '').trim();
      const upper = text.toUpperCase();
      if (!text) return;
      if (el.closest(`.${TAG_WRAP_CLASS}`)) return;
      if (upper === 'NEW') {
        el.remove();
      }
    });
  };

  const ensureProductCardText = (item, productWork) => {
    if (!item) return false;
    const wrapper = item.querySelector('.sa-079-ur-viewed-products-card-information__wrapper');
    const title = item.querySelector(TITLE_SELECTOR);
    if (!wrapper || !title) return false;

    wrapper.querySelectorAll('.ur-vendor').forEach((el) => el.remove());

    Array.from(wrapper.children).forEach((child) => {
      if (child === title) return;
      if (child.classList.contains('product-work')) return;
      if (child.matches('.sa-079-ur-viewed-products-price, .ur-stock-wrap, .ur-tags, .product-stock-wrap')) {
        return;
      }
      if (child.tagName === 'SPAN' || child.classList.contains('ur-vendor')) {
        child.remove();
      }
    });

    title.classList.add('product-name');

    const handle = getProductHandleFromItem(item);
    const work = String(productWork || item.dataset.productWork || readCachedProductWork(handle) || '').trim();
    if (work) {
      item.dataset.productWork = work;
      writeCachedProductWork(handle, work);
    }

    let workEl = wrapper.querySelector('.product-work');

    if (!work) {
      if (workEl) workEl.remove();
      return false;
    }

    if (!workEl) {
      workEl = document.createElement('p');
      workEl.className = 'product-work';
      wrapper.insertBefore(workEl, title);
    } else if (workEl.nextElementSibling !== title) {
      wrapper.insertBefore(workEl, title);
    }

    if (workEl.textContent !== work) {
      workEl.textContent = work;
    }

    return true;
  };

  const buildThemeTags = (tags, fallbackTexts = []) => {
    const source = [];
    if (Array.isArray(tags)) source.push(...tags);
    if (Array.isArray(fallbackTexts)) source.push(...fallbackTexts);
    if (!source.length) return [];
    const normalized = [];
    const seen = new Set();

    source.forEach((rawTag) => {
      const label = String(rawTag || '').trim();
      if (!label) return;
      const upper = label.toUpperCase();
      let cls = '';

      if (upper === 'NEW') {
        cls = 'ur-tag--new';
      } else if (label.includes('18禁') || upper.includes('R18') || upper.includes('18+')) {
        cls = 'ur-tag--18';
      } else if (
        label.includes('国内限定') ||
        label.includes('海外出荷不可') ||
        /JAPAN\s*ONLY/i.test(label) ||
        /JP\s*ONLY/i.test(label) ||
        /^SHIP$/i.test(label) ||
        /^TAG[_-]?SHIP$/i.test(label) ||
        /DOMESTIC/i.test(label)
      ) {
        cls = 'ur-tag--jp';
      }
      if (!cls) return;
      let normalizedLabel = label;
      if (cls === 'ur-tag--new') normalizedLabel = 'NEW';
      if (cls === 'ur-tag--18') normalizedLabel = '18禁';
      if (cls === 'ur-tag--jp') normalizedLabel = '国内限定';

      const key = `${cls}:${normalizedLabel}`;
      if (seen.has(key)) return;
      seen.add(key);
      normalized.push({ label: normalizedLabel, cls });
    });

    return normalized;
  };

  const ensureThemeTags = (item, json) => {
    if (!item) return;
    const tagClassElements = Array.from(item.querySelectorAll('.tag, [class*="tag-"]'));
    const classBasedTagTexts = tagClassElements
      .map((el) => (el.textContent || '').trim())
      .filter(Boolean);
    tagClassElements.forEach((el) => {
      const className = String(el.className || '').toLowerCase();
      if (className.includes('tag-ship')) classBasedTagTexts.push('国内限定');
      if (className.includes('tag-age')) classBasedTagTexts.push('18禁');
      if (className.includes('tag-new')) classBasedTagTexts.push('NEW');
    });
    const fallbackTexts = Array.from(item.querySelectorAll('span, div, p, a'))
      .map((el) => (el.textContent || '').trim())
      .filter(Boolean)
      .filter((text) => {
        const upper = text.toUpperCase();
        return (
          upper === 'NEW' ||
          text.includes('18禁') ||
          text.includes('国内限定') ||
          text.includes('海外出荷不可') ||
          /JAPAN\s*ONLY/i.test(text) ||
          /JP\s*ONLY/i.test(text)
        );
      });

    removeLegacyTagNodes(item);
    const infoWrapper = item.querySelector('.sa-079-ur-viewed-products-card-information__wrapper');
    if (!infoWrapper) return;

    const tags = buildThemeTags(json && json.tags, classBasedTagTexts.concat(fallbackTexts));
    if (!tags.length) return;

    const wrap = document.createElement('div');
    wrap.className = TAG_WRAP_CLASS;
    tags.forEach((tag) => {
      const el = document.createElement('span');
      el.className = `ur-tag ${tag.cls}`;
      el.textContent = tag.label;
      wrap.appendChild(el);
    });

    const stockWrap = item.querySelector(`.${STOCK_WRAP_CLASS}`);
    if (stockWrap && infoWrapper.contains(stockWrap)) {
      stockWrap.insertAdjacentElement('afterend', wrap);
    } else {
      infoWrapper.appendChild(wrap);
    }
  };

  const normalizePrice = (item, localeIsJa) => {
    const priceContainer = item.querySelector(PRICE_SELECTOR);
    if (!priceContainer) return;

    const compareEl = priceContainer.querySelector(COMPARE_SELECTOR);
    const currentEl = priceContainer.querySelector(CURRENT_SELECTOR);
    if (!currentEl) return;

    // Read the untaxed base price only once per element and remember it.
    // normalizePrice can run multiple times (0/500/1500ms + observer); reading
    // the already-rewritten text and re-applying tax would compound ×1.1 each
    // pass (2700 → 2970 → 3267 → 3594...). Always compute from the stored base.
    const readBaseRaw = (el) => {
      if (!el) return 0;
      if (el.dataset.urBaseRaw !== undefined && el.dataset.urBaseRaw !== '') {
        return Number(el.dataset.urBaseRaw) || 0;
      }
      const raw = getNumber(el.textContent);
      el.dataset.urBaseRaw = String(raw);
      return raw;
    };

    const currentRaw = readBaseRaw(currentEl);
    const compareRaw = compareEl ? readBaseRaw(compareEl) : 0;

    // Tax is applied regardless of locale, matching the tax-included display used
    // on product detail / cards (Liquid c-price-taxed). The tax label ("tax
    // included") is also shown in every locale, so the amount must be tax-included too.
    const currentDisplay = toTaxIncluded(currentRaw);
    const compareDisplay = compareRaw > 0 ? toTaxIncluded(compareRaw) : 0;

    currentEl.textContent = formatYen(currentDisplay);
    if (compareEl && compareDisplay > 0) {
      compareEl.textContent = formatYen(compareDisplay);
    }

    const existingTax = priceContainer.querySelector(`.${TAX_CLASS}`);
    const existingRate = priceContainer.querySelector(`.${DISCOUNT_CLASS}`);
    if (existingTax) existingTax.remove();
    if (existingRate) existingRate.remove();

    const taxEl = ensureTaxLabel(priceContainer);
    if (taxEl) {
      currentEl.insertAdjacentElement('afterend', taxEl);
    }

    if (compareDisplay > currentDisplay && compareDisplay > 0) {
      const rate = Math.round(((compareDisplay - currentDisplay) / compareDisplay) * 100);
      if (rate > 0) {
        const rateEl = document.createElement('span');
        rateEl.className = DISCOUNT_CLASS;
        rateEl.textContent = `${rate}%OFF`;
        priceContainer.appendChild(rateEl);
      }
    }
  };

  const ensureAgeGate = (item, json) => {
    if (!item || !json || !productTagsIncludeAdult(json.tags)) return;

    const message = getAgeGateMessage();
    item.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      link.setAttribute('data-age-check', '');
      if (!link.getAttribute('data-age-message')) {
        link.setAttribute('data-age-message', message);
      }
    });
  };

  const normalizeItem = async (item, localeIsJa) => {
    if (!item) return;

    const handle = getProductHandleFromItem(item);
    if (!handle) return;

    const [json, pageMeta] = await Promise.all([
      getProductJsonFromHandle(handle),
      getProductPageMetaFromHandle(handle),
    ]);

    const alreadyDone = item.dataset.urNormalized === '1';
    if (!alreadyDone) {
      normalizePrice(item, localeIsJa);
      ensureThemeTags(item, json);
      item.dataset.urNormalized = '1';
    }

    ensureProductCardText(item, (pageMeta && pageMeta.productWork) || '');
    if (!item.dataset.productWork) {
      productPageMetaCache.delete(handle);
      const retryMeta = await getProductPageMetaFromHandle(handle);
      ensureProductCardText(item, (retryMeta && retryMeta.productWork) || '');
      ensureStockStatus(item, json, retryMeta);
    } else {
      // Paint stock only after authoritative meta is ready (single apply).
      ensureStockStatus(item, json, pageMeta);
    }
    ensureAgeGate(item, json);
  };

  const gridNormalizingMap = new WeakMap();

  const normalizeAllInGrid = async (grid) => {
    if (!grid || gridNormalizingMap.get(grid)) return;
    gridNormalizingMap.set(grid, true);

    try {
      const localeIsJa = isJaLocale();
      const items = grid.querySelectorAll(ITEM_SELECTOR);
      if (!items.length) return;
      await Promise.all(Array.from(items).map((item) => normalizeItem(item, localeIsJa)));
      refreshUrStockStatusLabels();
      updateUrGridCarouselMode(grid);
    } finally {
      gridNormalizingMap.delete(grid);
    }
  };

  const UR_CAROUSEL_THRESHOLD = 5;
  const UR_SCROLLBAR_DRAG_SIZE_PX = 260;

  const clampUr01 = (value) => Math.min(1, Math.max(0, Number(value) || 0));

  const getUrScrollbarHost = (grid, section) => {
    if (grid && grid.classList.contains('ur-grid--carousel')) {
      const shell = grid.closest('.ur-carousel-shell');
      if (shell) return shell;
    }
    return section;
  };

  const relocateUrScrollbar = (grid, section) => {
    if (!section) return null;

    const host = getUrScrollbarHost(grid, section);
    let scrollbar = section.querySelector(':scope > .ur-scrollbar') || host.querySelector('.ur-scrollbar');
    if (!scrollbar) return null;

    if (scrollbar.parentElement !== host) {
      host.appendChild(scrollbar);
    }
    return scrollbar;
  };

  const ensureUrScrollbar = (grid, section) => {
    if (!section) return null;

    const host = getUrScrollbarHost(grid, section);
    let scrollbar = host.querySelector(':scope > .ur-scrollbar');
    if (scrollbar) return scrollbar;

    scrollbar = document.createElement('div');
    scrollbar.className = 'ur-scrollbar';
    scrollbar.setAttribute('aria-hidden', 'true');

    const thumb = document.createElement('div');
    thumb.className = 'ur-scrollbar__thumb';
    scrollbar.appendChild(thumb);

    host.appendChild(scrollbar);
    return scrollbar;
  };

  const destroyUrCarouselShell = (grid) => {
    if (!grid) return;

    const shell = grid.closest('.ur-carousel-shell');
    if (!shell) return;

    const section = grid.closest(SECTION_SELECTOR);
    const scrollbar = shell.querySelector('.ur-scrollbar');
    if (scrollbar && section) {
      section.appendChild(scrollbar);
    }

    const parent = shell.parentNode;
    if (parent) {
      parent.insertBefore(grid, shell);
      shell.remove();
    }
  };

  const getUrItemScrollLeft = (grid, index) => {
    const items = grid.querySelectorAll(ITEM_SELECTOR);
    const item = items[index];
    if (!item) return 0;
    return Math.max(0, item.offsetLeft);
  };

  const getUrActiveItemIndex = (grid) => {
    const items = grid.querySelectorAll(ITEM_SELECTOR);
    if (!items.length) return 0;

    const scrollLeft = grid.scrollLeft;
    let activeIndex = 0;
    items.forEach((item, index) => {
      if (item.offsetLeft <= scrollLeft + 1) {
        activeIndex = index;
      }
    });
    return activeIndex;
  };

  const scrollUrGrid = (grid, direction) => {
    if (!grid) return;

    const items = grid.querySelectorAll(ITEM_SELECTOR);
    if (!items.length) return;

    const maxScroll = Math.max(0, grid.scrollWidth - grid.clientWidth);
    const activeIndex = getUrActiveItemIndex(grid);
    const nextIndex = Math.max(0, Math.min(items.length - 1, activeIndex + direction));
    const nextLeft = Math.min(maxScroll, getUrItemScrollLeft(grid, nextIndex));

    grid.scrollTo({ left: nextLeft, behavior: 'smooth' });
  };

  const updateUrCarouselNav = (grid) => {
    const shell = grid ? grid.closest('.ur-carousel-shell') : null;
    if (!shell || !grid) return;

    const prev = shell.querySelector('.horizontal-swiper-nav--prev');
    const next = shell.querySelector('.horizontal-swiper-nav--next');
    const maxScroll = Math.max(0, grid.scrollWidth - grid.clientWidth);
    const canScroll = maxScroll > 1;

    if (prev) {
      prev.style.display = canScroll ? '' : 'none';
      prev.classList.toggle('swiper-button-disabled', canScroll && grid.scrollLeft <= 1);
    }
    if (next) {
      next.style.display = canScroll ? '' : 'none';
      next.classList.toggle('swiper-button-disabled', canScroll && grid.scrollLeft >= maxScroll - 1);
    }
  };

  const ensureUrCarouselShell = (section, grid) => {
    let shell = grid.closest('.ur-carousel-shell');
    if (shell) return shell;

    shell = document.createElement('div');
    shell.className = 'ur-carousel-shell';
    grid.parentNode.insertBefore(shell, grid);
    shell.appendChild(grid);
    section.classList.add('ur-section--has-shell');
    return shell;
  };

  const bindUrCarouselNav = (section, grid) => {
    if (!section || !grid) return;

    const shell = ensureUrCarouselShell(section, grid);
    relocateUrScrollbar(grid, section);
    if (shell.dataset.navBound === '1') {
      updateUrCarouselNav(grid);
      return;
    }

    const prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'horizontal-swiper-nav horizontal-swiper-nav--prev ur-carousel-nav';
    prevBtn.setAttribute('aria-label', '前へ');
    prevBtn.innerHTML = '<span aria-hidden="true">‹</span>';

    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'horizontal-swiper-nav horizontal-swiper-nav--next ur-carousel-nav';
    nextBtn.setAttribute('aria-label', '次へ');
    nextBtn.innerHTML = '<span aria-hidden="true">›</span>';

    shell.appendChild(prevBtn);
    shell.appendChild(nextBtn);

    prevBtn.addEventListener('click', () => scrollUrGrid(grid, -1));
    nextBtn.addEventListener('click', () => scrollUrGrid(grid, 1));

    const onScrollOrResize = () => {
      syncUrScrollbar(grid);
      updateUrCarouselNav(grid);
    };

    grid.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
    shell.dataset.navBound = '1';
    onScrollOrResize();
  };

  const updateUrGridCarouselMode = (grid) => {
    if (!grid) return;

    const section = grid.closest(SECTION_SELECTOR);
    const itemCount = grid.querySelectorAll(ITEM_SELECTOR).length;
    const enableCarousel = itemCount > UR_CAROUSEL_THRESHOLD;

    grid.classList.toggle('ur-grid--carousel', enableCarousel);
    grid.classList.toggle('ur-grid--static', !enableCarousel);
    if (section) {
      section.classList.toggle('ur-section--carousel', enableCarousel);
      section.classList.toggle('ur-section--static', !enableCarousel);
    }

    if (!enableCarousel) {
      destroyUrCarouselShell(grid);
      if (section) section.classList.remove('ur-section--has-shell');
      grid.scrollLeft = 0;
      grid.classList.remove('is-dragging');
      syncUrScrollbar(grid);
      return;
    }

    bindUrCarouselNav(section, grid);
    syncUrScrollbar(grid);
  };

  const syncUrScrollbar = (grid) => {
    if (!grid) return;
    const section = grid.closest(SECTION_SELECTOR);
    if (!section) return;

    relocateUrScrollbar(grid, section);
    const scrollbar = ensureUrScrollbar(grid, section);
    const thumb = scrollbar ? scrollbar.querySelector('.ur-scrollbar__thumb') : null;
    const client = grid.clientWidth || 0;
    const total = grid.scrollWidth || 0;
    if (client <= 0 || total <= 0) {
      if (scrollbar) scrollbar.style.display = 'none';
      return;
    }
    const maxScroll = Math.max(0, total - client);
    const enableCarousel = grid.classList.contains('ur-grid--carousel');
    if (scrollbar) {
      scrollbar.style.display = enableCarousel && maxScroll > 0 ? '' : 'none';
    }
    if (!enableCarousel || maxScroll <= 0) return;

    const trackWidth = scrollbar.getBoundingClientRect().width || client;
    if (trackWidth <= 0) return;

    const thumbSizePx = Math.min(trackWidth, UR_SCROLLBAR_DRAG_SIZE_PX);
    const thumbSize = (thumbSizePx / trackWidth) * 100;
    const maxLeft = Math.max(0, 100 - thumbSize);
    const progress = clampUr01(grid.scrollLeft / maxScroll);
    const thumbLeft = maxLeft * progress;

    section.style.setProperty('--ur-thumb-size', `${thumbSize}%`);
    section.style.setProperty('--ur-thumb-left', `${thumbLeft}%`);
    if (thumb) {
      thumb.style.width = `${thumbSize}%`;
      thumb.style.left = `${thumbLeft}%`;
    }
  };

  const bindScrollbarSync = (grid) => {
    if (!grid || grid.dataset.urScrollbarBound === '1') return;
    const handler = () => syncUrScrollbar(grid);
    const section = grid.closest(SECTION_SELECTOR);
    ensureUrScrollbar(grid, section);
    grid.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler, { passive: true });
    grid.dataset.urScrollbarBound = '1';
  };

  const bindScrollbarDrag = (grid) => {
    if (!grid) return;
    const section = grid.closest(SECTION_SELECTOR);
    const scrollbar = ensureUrScrollbar(grid, section);
    if (!scrollbar || scrollbarBoundMap.has(scrollbar)) return;

    let isDragging = false;
    let grabRatio = 0.5;

    const getUrThumbWidth = () => {
      const rect = scrollbar.getBoundingClientRect();
      const trackWidth = rect.width || 0;
      if (trackWidth <= 0) return 0;
      return Math.min(trackWidth, UR_SCROLLBAR_DRAG_SIZE_PX);
    };

    const setScrollFromPointer = (clientX) => {
      const rect = scrollbar.getBoundingClientRect();
      const thumbWidth = getUrThumbWidth();
      const maxThumbLeft = Math.max(0, rect.width - thumbWidth);
      const maxScroll = Math.max(0, grid.scrollWidth - grid.clientWidth);
      if (rect.width <= 0) return;

      let thumbLeft = clientX - rect.left - thumbWidth * grabRatio;
      thumbLeft = Math.max(0, Math.min(thumbLeft, maxThumbLeft));
      const progress = maxThumbLeft > 0 ? thumbLeft / maxThumbLeft : 0;
      grid.scrollLeft = maxScroll * progress;
      syncUrScrollbar(grid);
    };

    const stopDragging = () => {
      isDragging = false;
      scrollbar.classList.remove('is-dragging');
    };

    scrollbar.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      const thumb = scrollbar.querySelector('.ur-scrollbar__thumb');
      const thumbRect = thumb ? thumb.getBoundingClientRect() : null;

      if (thumbRect && event.clientX >= thumbRect.left && event.clientX <= thumbRect.right) {
        grabRatio = thumbRect.width > 0 ? (event.clientX - thumbRect.left) / thumbRect.width : 0.5;
      } else {
        grabRatio = 0.5;
      }

      isDragging = true;
      scrollbar.classList.add('is-dragging');
      if (scrollbar.setPointerCapture) {
        try {
          scrollbar.setPointerCapture(event.pointerId);
        } catch (e) {}
      }
      setScrollFromPointer(event.clientX);
      event.preventDefault();
    });

    scrollbar.addEventListener('pointermove', (event) => {
      if (!isDragging) return;
      setScrollFromPointer(event.clientX);
      event.preventDefault();
    });

    scrollbar.addEventListener('pointerup', stopDragging);
    scrollbar.addEventListener('pointercancel', stopDragging);
    scrollbar.addEventListener('lostpointercapture', stopDragging);
    scrollbarBoundMap.set(scrollbar, true);
  };

  const bindDragScroll = (grid) => {
    if (!grid || dragBoundMap.has(grid)) return;

    let isPointerDown = false;
    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;

    const stopDragging = () => {
      isPointerDown = false;
      requestAnimationFrame(() => {
        isDragging = false;
        grid.classList.remove('is-dragging');
      });
    };

    grid.addEventListener('pointerdown', (event) => {
      if (grid.classList.contains('ur-grid--static')) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      isPointerDown = true;
      isDragging = false;
      startX = event.clientX;
      startScrollLeft = grid.scrollLeft;
      grid.classList.add('is-dragging');
      if (grid.setPointerCapture) {
        try {
          grid.setPointerCapture(event.pointerId);
        } catch (e) {}
      }
    });

    grid.addEventListener('pointermove', (event) => {
      if (!isPointerDown) return;
      const deltaX = event.clientX - startX;
      if (!isDragging && Math.abs(deltaX) > 6) {
        isDragging = true;
      }
      if (!isDragging) return;
      grid.scrollLeft = startScrollLeft - deltaX;
      syncUrScrollbar(grid);
      event.preventDefault();
    });

    grid.addEventListener('pointerup', stopDragging);
    grid.addEventListener('pointercancel', stopDragging);
    grid.addEventListener('lostpointercapture', stopDragging);

    // クリックが効かない問題対策: ドラッグ判定でリンククリックを阻害しない
    // （横スクロールはマウスホイールまたはスクロールバーで行う想定）
    // grid.addEventListener(
    //   'click',
    //   (event) => {
    //     if (!isDragging) return;
    //     event.preventDefault();
    //     event.stopPropagation();
    //   },
    //   true
    // );

    dragBoundMap.set(grid, true);
  };

  const attachObserver = (grid) => {
    if (!grid || observerMap.has(grid)) return;

    const observer = new MutationObserver((mutations) => {
      const hasNewItems = mutations.some((mutation) => {
        if (mutation.type !== 'childList' || !mutation.addedNodes.length) return false;
        return Array.from(mutation.addedNodes).some((node) => {
          if (node.nodeType !== 1) return false;
          return node.matches(ITEM_SELECTOR) || Boolean(node.querySelector && node.querySelector(ITEM_SELECTOR));
        });
      });
      if (!hasNewItems) return;
      scheduleGridNormalize(grid, 120);
    });
    observer.observe(grid, { childList: true, subtree: true });
    observerMap.set(grid, observer);

    const section = grid.closest(SECTION_SELECTOR) || grid.closest('.sa-079-ur-viewed-products-section');
    if (!section || sectionObserverMap.has(section)) return;

    const sectionObserver = new MutationObserver((mutations) => {
      const becameVisible = mutations.some((mutation) => {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'style') return false;
        return isUrSectionVisible(section);
      });
      if (!becameVisible) return;
      scheduleGridNormalize(grid, 120);
    });
    sectionObserver.observe(section, {
      attributes: true,
      attributeFilter: ['style'],
    });
    sectionObserverMap.set(section, sectionObserver);
  };

  const initUrGrid = (grid) => {
    if (!grid) return;
    attachObserver(grid);
    bindScrollbarSync(grid);
    bindScrollbarDrag(grid);
    syncUrScrollbar(grid);
    scheduleGridNormalize(grid, 0);
    [300, 800, 1500, 3000, 5000, 8000].forEach((delayMs) => {
      window.setTimeout(() => {
        normalizeAllInGrid(grid).then(() => syncUrScrollbar(grid));
      }, delayMs);
    });
  };

  const boot = () => {
    localizeRecentlyViewedHeading();
    seedProductWorkCacheFromPage();
    const currentWorkEl = document.querySelector('.detail-stock[data-product-work]');
    if (currentWorkEl) {
      const currentWork = String(currentWorkEl.getAttribute('data-product-work') || '').trim();
      try {
        const parts = window.location.pathname.split('/products/');
        const fromPath = parts[1] ? decodeURIComponent(parts[1]).split('/')[0] : '';
        if (fromPath && currentWork) writeCachedProductWork(fromPath, currentWork);
      } catch (e) {
        // noop
      }
    }
    findUrGrids().forEach((grid) => initUrGrid(grid));
  };

  const observeUrSectionsGlobally = () => {
    if (document.documentElement.dataset.urGlobalObserverBound === '1') return;
    document.documentElement.dataset.urGlobalObserverBound = '1';

    let timerId = 0;
    const scheduleGlobalUrRefresh = () => {
      if (timerId) window.clearTimeout(timerId);
      timerId = window.setTimeout(() => {
        timerId = 0;
        seedProductWorkCacheFromPage();
        findUrGrids().forEach((grid) => {
          if (!observerMap.has(grid)) {
            initUrGrid(grid);
            return;
          }
          scheduleGridNormalize(grid, 0);
        });
      }, 120);
    };

    const observer = new MutationObserver((mutations) => {
      const shouldRefresh = mutations.some((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const target = mutation.target;
          if (
            target &&
            target.matches &&
            (target.matches(SECTION_SELECTOR) || target.closest(SECTION_SELECTOR))
          ) {
            return isUrSectionVisible(target.closest(SECTION_SELECTOR) || target);
          }
        }

        if (mutation.type !== 'childList' || !mutation.addedNodes.length) return false;

        return Array.from(mutation.addedNodes).some((node) => {
          if (node.nodeType !== 1) return false;
          return (
            node.matches(GRID_SELECTOR) ||
            node.matches(ITEM_SELECTOR) ||
            node.matches(SECTION_SELECTOR) ||
            Boolean(node.querySelector && node.querySelector(`${GRID_SELECTOR}, ${ITEM_SELECTOR}`))
          );
        });
      });

      if (shouldRefresh) scheduleGlobalUrRefresh();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      boot();
      observeUrSectionsGlobally();
    });
  } else {
    window.setTimeout(() => {
      boot();
      observeUrSectionsGlobally();
    }, 0);
  }

  window.addEventListener('load', () => {
    seedProductWorkCacheFromPage();
    findUrGrids().forEach((grid) => scheduleGridNormalize(grid, 0));
    [500, 1500, 3500].forEach((delayMs) => {
      window.setTimeout(() => {
        seedProductWorkCacheFromPage();
        findUrGrids().forEach((grid) => scheduleGridNormalize(grid, 0));
      }, delayMs);
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    window.setTimeout(() => {
      boot();
      if (event && event.target) seedProductWorkCacheFromPage(event.target);
    }, 0);
  });
})();

/**
 * Rank King slider scrollbar sync
 * Keeps custom black thumb aligned with horizontal scroll position.
 */
(() => {
  const VIEWPORT_SELECTOR = '[id^="ranking-list--"].ranking-list .ranking-list__slider-viewport';
  const desktopMq = window.matchMedia('(min-width: 768px)');
  const observerMap = new WeakMap();

  const clamp01 = (value) => Math.min(1, Math.max(0, Number(value) || 0));

  const extractTranslateX = (el) => {
    if (!el) return 0;
    const inline = el.style && el.style.transform ? el.style.transform : '';
    const fromInline = inline.match(/translate3d\(\s*([-.\d]+)px/i) || inline.match(/translateX\(\s*([-.\d]+)px/i);
    if (fromInline && fromInline[1] != null) return parseFloat(fromInline[1]) || 0;

    const computed = window.getComputedStyle(el).transform;
    if (!computed || computed === 'none') return 0;
    const matrix3d = computed.match(/^matrix3d\((.+)\)$/);
    if (matrix3d) {
      const values = matrix3d[1].split(',').map((n) => parseFloat(n.trim()));
      return values.length === 16 ? (values[12] || 0) : 0;
    }
    const matrix2d = computed.match(/^matrix\((.+)\)$/);
    if (matrix2d) {
      const values = matrix2d[1].split(',').map((n) => parseFloat(n.trim()));
      return values.length === 6 ? (values[4] || 0) : 0;
    }
    return 0;
  };

  const syncRankingScrollbar = (viewport) => {
    if (!viewport) return;
    const list = viewport.closest('[id^="ranking-list--"].ranking-list');
    if (!list) return;

    const client = viewport.clientWidth || 0;
    const total = viewport.scrollWidth || 0;
    if (client <= 0 || total <= 0) return;

    const thumbSize = desktopMq.matches ? 20 : 50;
    const maxLeft = Math.max(0, 100 - thumbSize);
    const maxScroll = Math.max(0, total - client);

    const grid = viewport.querySelector('.ranking-list__grid');
    const translateX = extractTranslateX(grid);
    const virtualMax = Math.max(0, (grid ? grid.scrollWidth : 0) - client);
    const scrollProgress = maxScroll > 0 ? clamp01(viewport.scrollLeft / maxScroll) : 0;
    const transformProgress = virtualMax > 0 && translateX < 0 ? clamp01((-translateX) / virtualMax) : 0;
    const progress = Math.max(scrollProgress, transformProgress);
    const thumbLeft = maxLeft * progress;

    list.style.setProperty('--ranking-thumb-size', `${thumbSize}%`);
    list.style.setProperty('--ranking-thumb-left', `${thumbLeft}%`);
  };

  const bindRankingViewport = (viewport) => {
    if (!viewport || viewport.dataset.rankingScrollbarBound === '1') return;
    const handler = () => syncRankingScrollbar(viewport);
    viewport.addEventListener('scroll', handler, { passive: true });
    window.addEventListener('resize', handler, { passive: true });
    window.addEventListener('touchend', handler, { passive: true });
    window.addEventListener('mouseup', handler, { passive: true });

    const grid = viewport.querySelector('.ranking-list__grid');
    if (grid && !observerMap.has(grid)) {
      const observer = new MutationObserver(handler);
      observer.observe(grid, { attributes: true, attributeFilter: ['style', 'class'] });
      observerMap.set(grid, observer);
    }

    viewport.dataset.rankingScrollbarBound = '1';
    syncRankingScrollbar(viewport);
  };

  const bootRankingScrollbars = () => {
    document.querySelectorAll(VIEWPORT_SELECTOR).forEach((viewport) => {
      bindRankingViewport(viewport);
      syncRankingScrollbar(viewport);
      setTimeout(() => syncRankingScrollbar(viewport), 500);
      setTimeout(() => syncRankingScrollbar(viewport), 1500);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootRankingScrollbars);
  } else {
    bootRankingScrollbars();
  }
})();
